import type { DeskDetail, DeskMetric } from '@deskworks/shared-types';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { intlLocale, t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { Star, Target, TrendingUp, Zap } from 'lucide-react';
import { ActivityBars, RangeSwitch, rangeLabel, useActivityRange, WorkItemCounts } from '@/components/desk/activity';
import { deskTone } from '@/components/desk/desk-look';
import { formatMetric, MetricSparkline, MetricTrend, OverlayLegend } from '@/components/desk/pulse';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { cn } from '@/lib/utils';

/**
 * The desk's activity stream (24h · 7d · 30d) and the time series the Lead keeps for it (record_metric), fitting its
 * goals (docs/features/desk-pulse.md).
 */
export function DeskMetricsSection({ desk }: { desk: DeskDetail }) {
  const tone = deskTone(desk.id);
  const [range] = useActivityRange();
  const buckets = desk.pulse?.activity?.[range] ?? [];
  const total = buckets.reduce((a, b) => a + b, 0);
  const live = desk.pulse?.state === 'working' || desk.pulse?.state === 'checking';
  const overlay = desk.metrics.find((m) => m.overlay) ?? null;
  return (
    <Section
      icon={<TrendingUp />}
      title={t('Activity & metrics')}
      description={t('What happened on the desk, and the numbers the Lead records on every check to show progress towards its goals.')}
      actions={<RangeSwitch />}
    >
      <div className="mb-3 rounded-xl border bg-card p-3.5">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl leading-none font-semibold tabular-nums">{total.toLocaleString(intlLocale)}</span>
            <span className="text-xs text-muted-foreground">
              {total === 1 ? t('event · {range}', { range: rangeLabel(range) }) : t('events · {range}', { range: rangeLabel(range) })}
            </span>
          </div>
          <WorkItemCounts items={desk.pulse?.workItems} range={range} className="ml-auto text-xs" />
        </div>
        <ActivityBars buckets={buckets} range={range} live={live} className={cn('mt-3 h-16 w-full gap-[3px]', tone.text)} />
      </div>
      {desk.metrics.length === 0 ? (
        <div className="flex items-center gap-4 rounded-lg border border-dashed bg-muted/20 px-4 py-3">
          <MetricSparkline points={[]} className={cn('h-10 w-28', tone.text)} />
          <p className="text-sm text-muted-foreground">
            {desk.goals[0]
              ? t('No metrics yet. On its next check the Lead chooses numbers that fit the goals (e.g. for “{goal}”) and starts recording them.', { goal: desk.goals[0].title })
              : t('No metrics yet. On its next check the Lead chooses numbers that fit the goals and starts recording them.')}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {desk.metrics.map((m) => (
            <div key={m.id} className={cn('rounded-xl border bg-card p-3.5', m.headline && 'ring-1 ring-primary/15')}>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">{m.name}</span>
                {m.query && (
                  <span title={t('Deskworks measures it itself whenever the workspace database changes')} className="inline-flex items-center gap-0.5 text-[11px] text-emerald-700 dark:text-emerald-400">
                    <Zap className="size-3" /> Live
                  </span>
                )}
                {(m.headline || m.overlay || (m.card && m.query)) && (
                  <span
                    title={m.headline ? t('The chart on the desk’s card') : m.overlay ? t('A second line in the card’s chart, over the headline') : t('A figure on the desk’s card')}
                    className="inline-flex items-center gap-1 text-[11px]"
                  >
                    <Star className="size-3 fill-current text-amber-500" /> {m.headline ? t('Card chart') : m.overlay ? t('Over card chart') : t('On card')}
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl leading-none font-semibold tabular-nums">{m.latest ? formatMetric(m.latest.value, m.unit) : '—'}</span>
                <MetricTrend metric={m} />
              </div>
              <MetricSparkline points={m.points} metric={m} overlay={m.headline ? overlay : null} className={cn('mt-3 h-14 w-full', tone.text)} />
              {m.headline && overlay && <OverlayLegend overlay={overlay} className="mt-1.5 max-w-full text-[11px] text-muted-foreground" />}
              <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                {m.goalTitle && (
                  <span className="inline-flex min-w-0 items-center gap-1" title={t('Goal: {goal}', { goal: m.goalTitle })}>
                    <Target className="size-3 shrink-0" />
                    <span className="truncate">{m.goalTitle}</span>
                  </span>
                )}
                <span className="ml-auto shrink-0">
                  {m.query
                    ? tr('changed {time}', { time: <RelativeTime value={m.latest?.at} /> })
                    : m.points.length === 1
                      ? tr('1 value · {time}', { time: <RelativeTime value={m.latest?.at} /> })
                      : tr('{n} values · {time}', { n: m.points.length, time: <RelativeTime value={m.latest?.at} /> })}
                </span>
              </div>
              {m.queryError && (
                <p className="mt-2 rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                  {t('Its query fails, so this is the last good value: {error}', { error: m.queryError })}
                </p>
              )}
              {m.query && (
                <details className="mt-2 text-[11px] text-muted-foreground">
                  <summary className="cursor-pointer">{t('Query')}</summary>
                  <pre className="mt-1 overflow-auto rounded-md bg-muted/50 p-2 font-mono whitespace-pre-wrap">{m.query}</pre>
                </details>
              )}
              <MetricTarget desk={desk} metric={m} />
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/** Where a metric should get, set by people; the Lead reports against it (docs/features/desk-reports.md). */
function MetricTarget({ desk, metric }: { desk: DeskDetail; metric: DeskMetric }) {
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState(metric.target == null ? '' : String(metric.target));
  const [by, setBy] = useState(metric.targetBy ?? '');
  const save = useApiMutation((value: number | null) => api.desks.setMetricTarget(desk.id, metric.id, { target: value, targetBy: value == null ? null : by || null }), {
    invalidate: [qk.desk(desk.id)],
    success: (_d, value) => (value == null ? t('Target removed') : t('Target set')),
    onSuccess: () => setEditing(false),
  });
  const sign = metric.good === 'down' ? '≤' : metric.good === 'up' ? '≥' : '→';
  const reached =
    metric.target != null && metric.latest != null && (metric.good === 'down' ? metric.latest.value <= metric.target : metric.latest.value >= metric.target);
  if (editing) {
    const value = Number(target.replace(',', '.'));
    const valid = target.trim() !== '' && Number.isFinite(value);
    return (
      <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t pt-2">
        <span className="text-[11px] text-muted-foreground">{t('Target {sign}', { sign })}</span>
        <Input value={target} onChange={(e) => setTarget(e.target.value)} inputMode="decimal" className="h-7 w-20 text-xs" autoFocus />
        <span className="text-[11px] text-muted-foreground">{t('by')}</span>
        <Input type="date" value={by} onChange={(e) => setBy(e.target.value)} className="h-7 w-36 text-xs" />
        <Button size="xs" onClick={() => save.mutate(value)} disabled={!valid} loading={save.isPending}>
          {t('Save')}
        </Button>
        {metric.target != null && (
          <Button size="xs" variant="ghost" onClick={() => save.mutate(null)}>
            {t('Remove')}
          </Button>
        )}
        <Button size="xs" variant="ghost" onClick={() => setEditing(false)}>
          {t('Cancel')}
        </Button>
      </div>
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} className="mt-2 w-full border-t pt-2 text-left text-[11px] text-muted-foreground hover:text-foreground">
      {metric.target == null ? (
        t('+ Set a target')
      ) : (
        <span className={cn(reached && 'text-emerald-600 dark:text-emerald-400')}>
          {t('Target {sign} {value}', { sign, value: formatMetric(metric.target, metric.unit) })}
          {metric.targetBy ? ` ${t('by {date}', { date: new Date(metric.targetBy).toLocaleDateString(intlLocale, { day: 'numeric', month: 'short', year: 'numeric' }) })}` : ''}
          {reached ? ` · ${t('reached')}` : ''}
        </span>
      )}
    </button>
  );
}
